import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  Skeleton,
  Alert,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
  Button,
  TextField,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import SaveIcon from '@mui/icons-material/Save'
import GridViewIcon from '@mui/icons-material/GridView'
import ViewListIcon from '@mui/icons-material/ViewList'
import FavoriteIcon from '@mui/icons-material/Favorite'
import { MoviePoster, getProxiedImageUrl, FALLBACK_POSTER_URL } from '@aperture/ui'
import { Breadcrumbs } from '@/components/Breadcrumbs'

interface User {
  id: string
  username: string
  display_name: string | null
  provider: 'emby' | 'jellyfin'
  is_admin: boolean
  is_enabled: boolean
  movies_enabled: boolean
  series_enabled: boolean
  discover_enabled: boolean
  discover_request_enabled: boolean
  can_manage_watch_history: boolean
  seerr_user_id: number | null
  created_at: string
}

interface Recommendation {
  movie_id: string
  rank: number
  final_score: number
  movie: {
    id: string
    title: string
    year: number | null
    poster_url: string | null
    genres: string[]
  }
}

interface WatchHistoryItem {
  movie_id: string
  play_count: number
  is_favorite: boolean
  last_played_at: string | null
  title: string
  year: number | null
  poster_url: string | null
  genres: string[]
  community_rating: number | null
  overview: string | null
}

interface WatchHistoryResponse {
  history: WatchHistoryItem[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box pt={3}>{children}</Box>}
    </div>
  )
}

// User Settings Tab Component (Admin only)
function UserSettingsTab({ userId, user }: { userId: string; user: User }) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingWatchHistory, setSavingWatchHistory] = useState(false)
  const [savingDiscovery, setSavingDiscovery] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [canManageWatchHistory, setCanManageWatchHistory] = useState(user.can_manage_watch_history)
  const [discoverEnabled, setDiscoverEnabled] = useState(user.discover_enabled)
  const [discoverRequestEnabled, setDiscoverRequestEnabled] = useState(user.discover_request_enabled)
  const [seerrUserIdInput, setSeerrUserIdInput] = useState(
    user.seerr_user_id != null ? String(user.seerr_user_id) : ''
  )
  const [savingSeerrId, setSavingSeerrId] = useState(false)
  const [settings, setSettings] = useState<{
    overrideAllowed: boolean
    enabled: boolean | null
    effectiveValue: boolean
    globalConfig: { enabled: boolean; userOverrideAllowed: boolean }
  } | null>(null)

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true)
      const response = await fetch(`/api/settings/ai-explanation/user/${userId}`, {
        credentials: 'include',
      })
      if (response.ok) {
        const data = await response.json()
        setSettings(data)
      }
    } catch (err) {
      console.error('Failed to fetch user AI settings:', err)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const handleSave = async () => {
    if (!settings) return
    try {
      setSaving(true)
      setError(null)
      const response = await fetch(`/api/settings/ai-explanation/user/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrideAllowed: settings.overrideAllowed }),
        credentials: 'include',
      })
      if (!response.ok) throw new Error(t('admin.userDetail.errorSaveSettings'))
      const data = await response.json()
      setSettings({
        ...settings,
        effectiveValue: data.effectiveValue,
      })
      setSuccess(t('admin.userDetail.successSaveSettings'))
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.userDetail.unknownError'))
    } finally {
      setSaving(false)
    }
  }

  const handleWatchHistoryToggle = async (enabled: boolean) => {
    try {
      setSavingWatchHistory(true)
      setError(null)
      const response = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ canManageWatchHistory: enabled }),
        credentials: 'include',
      })
      if (!response.ok) throw new Error(t('admin.userDetail.errorUpdatePermission'))
      setCanManageWatchHistory(enabled)
      setSuccess(t('admin.userDetail.successWatchHistoryPerm'))
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.userDetail.unknownError'))
      // Revert the toggle on error
      setCanManageWatchHistory(!enabled)
    } finally {
      setSavingWatchHistory(false)
    }
  }

  const handleSaveSeerrUserId = async () => {
    const trimmed = seerrUserIdInput.trim()
    let seerrUserId: number | null
    if (trimmed === '') {
      seerrUserId = null
    } else {
      const n = parseInt(trimmed, 10)
      if (Number.isNaN(n) || n < 1) {
        setError(t('admin.userDetail.seerrIdInvalid'))
        return
      }
      seerrUserId = n
    }
    try {
      setSavingSeerrId(true)
      setError(null)
      const response = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seerrUserId }),
        credentials: 'include',
      })
      if (!response.ok) throw new Error(t('admin.userDetail.errorUpdateSeerr'))
      setSuccess(t('admin.userDetail.successSeerrSaved'))
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.userDetail.unknownError'))
    } finally {
      setSavingSeerrId(false)
    }
  }

  const handleDiscoveryToggle = async (field: 'discover' | 'request', enabled: boolean) => {
    try {
      setSavingDiscovery(true)
      setError(null)
      const body = field === 'discover' 
        ? { discoverEnabled: enabled }
        : { discoverRequestEnabled: enabled }
      
      const response = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      })
      if (!response.ok) throw new Error(t('admin.userDetail.errorUpdatePermission'))
      
      if (field === 'discover') {
        setDiscoverEnabled(enabled)
        // If disabling discovery, also disable request permission
        if (!enabled) {
          setDiscoverRequestEnabled(false)
        }
      } else {
        setDiscoverRequestEnabled(enabled)
      }
      
      setSuccess(
        field === 'discover'
          ? t('admin.userDetail.discoveryAccessUpdated')
          : t('admin.userDetail.discoveryRequestUpdated')
      )
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.userDetail.unknownError'))
      // Revert the toggle on error
      if (field === 'discover') {
        setDiscoverEnabled(!enabled)
      } else {
        setDiscoverRequestEnabled(!enabled)
      }
    } finally {
      setSavingDiscovery(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (!settings) {
    return (
      <Alert severity="error">{t('admin.userDetail.settingsLoadFailed')}</Alert>
    )
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Watch History Management Permission */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {t('admin.userDetail.watchHistoryTitle')}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.userDetail.watchHistoryBody')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={canManageWatchHistory}
                onChange={(e) => handleWatchHistoryToggle(e.target.checked)}
                disabled={savingWatchHistory}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {t('admin.userDetail.watchHistoryToggleTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.userDetail.watchHistoryToggleHint')}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', ml: 0 }}
          />

          {savingWatchHistory && (
            <Box display="flex" alignItems="center" gap={1} mt={2}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">{t('common.saving')}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Discovery Permissions */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <HubOutlinedIcon sx={{ color: '#ec4899' }} />
            {t('admin.userDetail.discoveryTitle')}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.userDetail.discoveryBody')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={discoverEnabled}
                onChange={(e) => handleDiscoveryToggle('discover', e.target.checked)}
                disabled={savingDiscovery}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {t('admin.userDetail.discoverySuggestionsTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.userDetail.discoverySuggestionsHint')}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', ml: 0, mb: 2 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={discoverRequestEnabled}
                onChange={(e) => handleDiscoveryToggle('request', e.target.checked)}
                disabled={savingDiscovery || !discoverEnabled}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {t('admin.userDetail.discoveryRequestTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.userDetail.discoveryRequestHint')}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', ml: 0 }}
          />

          {!discoverEnabled && discoverRequestEnabled && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('admin.userDetail.discoveryRequiresDiscover')}
            </Alert>
          )}

          {savingDiscovery && (
            <Box display="flex" alignItems="center" gap={1} mt={2}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">{t('common.saving')}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('admin.userDetail.seerrMappingTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.userDetail.seerrMappingBody')}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start">
            <TextField
              label={t('admin.userDetail.seerrUserIdLabel')}
              value={seerrUserIdInput}
              onChange={(e) => setSeerrUserIdInput(e.target.value)}
              placeholder={t('admin.userDetail.seerrUserIdPlaceholder')}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <Button
              variant="outlined"
              onClick={handleSaveSeerrUserId}
              disabled={savingSeerrId}
              startIcon={savingSeerrId ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              {savingSeerrId ? t('common.saving') : t('common.save')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AutoAwesomeIcon color="primary" />
            {t('admin.userDetail.aiExplanationTitle')}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.userDetail.aiExplanationBody')}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Global Setting Status */}
          <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {settings.globalConfig.enabled
                ? settings.globalConfig.userOverrideAllowed
                  ? t('admin.userDetail.globalSettingEnabledAllowed')
                  : t('admin.userDetail.globalSettingEnabledDisallowed')
                : settings.globalConfig.userOverrideAllowed
                  ? t('admin.userDetail.globalSettingDisabledAllowed')
                  : t('admin.userDetail.globalSettingDisabledDisallowed')}
            </Typography>
          </Box>

          {/* Override Permission Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={settings.overrideAllowed}
                onChange={(e) => setSettings({ ...settings, overrideAllowed: e.target.checked })}
                disabled={!settings.globalConfig.userOverrideAllowed}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {t('admin.userDetail.allowOverrideTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.userDetail.allowOverrideHint')}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', ml: 0, mb: 2 }}
          />

          {!settings.globalConfig.userOverrideAllowed && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('admin.userDetail.overridesDisabledGlobally')}
            </Alert>
          )}

          {/* Current Effective Value */}
          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, mb: 2 }}>
            <Typography variant="body2">
              <strong>{t('admin.userDetail.effectiveValueLabel')}</strong>{' '}
              {settings.effectiveValue
                ? t('admin.userDetail.effectiveIncluded')
                : t('admin.userDetail.effectiveExcluded')}
            </Typography>
            {settings.overrideAllowed && settings.enabled !== null && (
              <Typography variant="caption" color="text.secondary">
                {settings.enabled ? t('admin.userDetail.userChoseEnable') : t('admin.userDetail.userChoseDisable')}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || !settings.globalConfig.userOverrideAllowed}
            >
              {saving ? t('common.saving') : t('admin.userDetail.saveChanges')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

// Watch History Tab Component
function WatchHistoryTab({ userId }: { userId: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [history, setHistory] = useState<WatchHistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 1 })
  const [sortBy, setSortBy] = useState<'recent' | 'plays' | 'title'>('recent')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const fetchHistory = useCallback(async (page: number, sort: string) => {
    setLoading(true)
    try {
      const response = await fetch(
        `/api/users/${userId}/watch-history?page=${page}&pageSize=50&sortBy=${sort}`,
        { credentials: 'include' }
      )
      if (response.ok) {
        const data: WatchHistoryResponse = await response.json()
        setHistory(data.history)
        setPagination(data.pagination)
      }
    } catch (err) {
      console.error('Failed to fetch watch history:', err)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    fetchHistory(pagination.page, sortBy)
  }, [fetchHistory, sortBy, pagination.page])

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchHistory(page, sortBy)
  }

  const handleSortChange = (_: React.MouseEvent<HTMLElement>, newSort: 'recent' | 'plays' | 'title' | null) => {
    if (newSort) {
      setSortBy(newSort)
      fetchHistory(1, newSort)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return t('admin.userDetail.dateNever')
    const date = new Date(dateStr)
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return t('admin.userDetail.dateToday')
    if (diffDays === 1) return t('admin.userDetail.dateYesterday')
    if (diffDays < 7) return t('admin.userDetail.dateDaysAgo', { count: diffDays })
    if (diffDays < 30) return t('admin.userDetail.dateWeeksAgo', { count: Math.floor(diffDays / 7) })
    if (diffDays < 365) return t('admin.userDetail.dateMonthsAgo', { count: Math.floor(diffDays / 30) })
    return date.toLocaleDateString()
  }

  if (loading && history.length === 0) {
    return (
      <Box>
        <Grid container spacing={2}>
          {[...Array(12)].map((_, i) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
              <Skeleton variant="rectangular" sx={{ width: '100%', aspectRatio: '2/3', borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    )
  }

  if (history.length === 0) {
    return (
      <Typography color="text.secondary">
        {t('admin.userDetail.watchHistoryTabEmpty')}
      </Typography>
    )
  }

  return (
    <Box>
      {/* Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            {t('admin.userDetail.moviesWatchedCount', { count: pagination.total })}
          </Typography>
          <ToggleButtonGroup
            value={sortBy}
            exclusive
            onChange={handleSortChange}
            size="small"
          >
            <ToggleButton value="recent">{t('admin.userDetail.sortRecent')}</ToggleButton>
            <ToggleButton value="plays">{t('admin.userDetail.sortMostPlayed')}</ToggleButton>
            <ToggleButton value="title">{t('admin.userDetail.sortTitleAz')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {loading && <CircularProgress size={20} />}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="grid"><GridViewIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <Grid container spacing={2}>
          {history.map((item) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={item.movie_id}>
              <Box position="relative">
                <MoviePoster
                  title={item.title}
                  year={item.year}
                  posterUrl={item.poster_url}
                  genres={item.genres}
                  rating={item.community_rating}
                  overview={item.overview}
                  responsive
                  onClick={() => navigate(`/movies/${item.movie_id}`)}
                />
                {/* Play count badge - cap display at 5x, show "Rewatched" for higher */}
                {item.play_count > 1 && (
                  <Chip
                    label={item.play_count <= 5 ? `${item.play_count}x` : t('admin.userDetail.rewatched')}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      backgroundColor: 'primary.main',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 22,
                    }}
                  />
                )}
                {/* Favorite badge */}
                {item.is_favorite && (
                  <FavoriteIcon
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      color: 'error.main',
                      fontSize: 20,
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
                    }}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.userDetail.tableMovie')}</TableCell>
                <TableCell align="center">{t('admin.userDetail.tablePlays')}</TableCell>
                <TableCell align="center">{t('admin.userDetail.tableRating')}</TableCell>
                <TableCell align="right">{t('admin.userDetail.tableLastWatched')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((item) => (
                <TableRow 
                  key={item.movie_id} 
                  hover 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/movies/${item.movie_id}`)}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        component="img"
                        src={getProxiedImageUrl(item.poster_url)}
                        alt={item.title}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.src = FALLBACK_POSTER_URL
                        }}
                        sx={{
                          width: 40,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 0.5,
                          backgroundColor: 'grey.800',
                        }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {item.title}
                          {item.is_favorite && (
                            <FavoriteIcon sx={{ ml: 0.5, fontSize: 14, color: 'error.main', verticalAlign: 'middle' }} />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.year} • {item.genres?.slice(0, 2).join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={item.play_count <= 5 ? item.play_count : t('admin.userDetail.playsBadgeMany')} 
                      size="small" 
                      color={item.play_count > 3 ? 'primary' : 'default'}
                      variant={item.play_count > 1 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {item.community_rating ? (
                      <Typography variant="body2">
                        {Number(item.community_rating).toFixed(1)}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(item.last_played_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  )
}

// Tab name mapping
const TAB_NAMES = ['recommendations', 'history', 'playlists', 'diagnostics', 'settings'] as const
type TabName = typeof TAB_NAMES[number]

const getTabIndex = (tabName: string | null): number => {
  if (!tabName) return 0
  const index = TAB_NAMES.indexOf(tabName as TabName)
  return index >= 0 ? index : 0
}

const getTabName = (index: number): TabName => {
  return TAB_NAMES[index] || 'recommendations'
}

export function UserDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Get current tab from URL
  const currentTab = searchParams.get('tab')
  const tabValue = getTabIndex(currentTab)
  
  // Handle tab change - update URL which will update the tab
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const tabName = getTabName(newValue)
    if (tabName === 'recommendations') {
      // Remove tab param for default tab to keep URL clean
      setSearchParams({}, { replace: false })
    } else {
      setSearchParams({ tab: tabName }, { replace: false })
    }
  }
  
  const [user, setUser] = useState<User | null>(null)
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user
        const userResponse = await fetch(`/api/users/${id}`, { credentials: 'include' })
        if (!userResponse.ok) {
          setError(t('admin.userDetail.userNotFound'))
          return
        }
        const userData = await userResponse.json()
        setUser(userData)

        // Fetch recommendations
        const recsResponse = await fetch(`/api/recommendations/${id}`, { credentials: 'include' })
        if (recsResponse.ok) {
          const recsData = await recsResponse.json()
          setRecommendations(recsData.recommendations || [])
        }
      } catch {
        setError(t('admin.userDetail.couldNotLoadUser'))
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id, t])

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="rectangular" height={400} sx={{ mt: 2 }} />
      </Box>
    )
  }

  if (error || !user) {
    return (
      <Box>
        <Breadcrumbs
          items={[
            { label: t('nav.admin'), path: '/admin/users' },
            { label: t('admin.users'), path: '/admin/users' },
            { label: t('admin.userDetail.breadcrumbUser') },
          ]}
        />
        <Alert severity="error">{error || t('admin.userDetail.userNotFound')}</Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.admin'), path: '/admin/users' },
          { label: t('admin.users'), path: '/admin/users' },
        ]}
        currentLabel={user.display_name || user.username}
      />

      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Typography variant="h5" fontWeight={700}>
          {user.display_name || user.username}
        </Typography>
        {user.is_admin && <Chip label={t('admin.usersPage.adminChip')} size="small" color="primary" />}
        {user.movies_enabled && (
          <Chip label={t('admin.usersPage.movies')} size="small" color="success" variant="filled" />
        )}
        {user.series_enabled && (
          <Chip label={t('admin.usersPage.series')} size="small" color="success" variant="filled" />
        )}
        {!user.movies_enabled && !user.series_enabled && (
          <Chip label={t('admin.userDetail.chipAiDisabled')} size="small" color="default" variant="outlined" />
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('admin.userDetail.joinedLine', {
          username: user.username,
          provider: user.provider,
          joined: new Date(user.created_at).toLocaleDateString(),
        })}
      </Typography>

      <Paper sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ px: 2, pt: 1 }}>
          <Tab label={t('admin.userDetail.tabRecommendations')} />
          <Tab label={t('admin.userDetail.tabWatchHistory')} />
          <Tab label={t('admin.userDetail.tabSettings')} />
        </Tabs>

        <Box p={3}>
          <TabPanel value={tabValue} index={0}>
            {recommendations.length === 0 ? (
              <Typography color="text.secondary">
                {t('admin.userDetail.recsEmpty')}
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {recommendations.map((rec) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={rec.movie_id}>
                    <MoviePoster
                      title={rec.movie.title}
                      year={rec.movie.year}
                      posterUrl={rec.movie.poster_url}
                      genres={rec.movie.genres}
                      score={rec.final_score}
                      showScore
                      responsive
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <WatchHistoryTab userId={user.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <UserSettingsTab userId={user.id} user={user} />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  )
}

